import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PhoneCall, Search } from 'lucide-react';
import { Lead } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';

const PER_PAGE = 20;

const phoneStatusLabels: Record<string, string> = {
  connected: 'Terhubung',
  not_connected: 'Tidak terhubung',
  invalid: 'Nomor tidak valid',
};

const outcomeLabels: Record<string, string> = {
  closing: 'Closing',
  call_back_later: 'Minta dihubungi kembali',
  other: 'Lainnya',
};

const BUSINESS_TYPE_OPTIONS = [
  { value: '', label: 'Semua' },
  { value: 'retail', label: 'Retail' },
  { value: 'fnb', label: 'F&B' },
  { value: 'fashion', label: 'Fashion' },
  { value: 'bengkel', label: 'Bengkel' },
  { value: 'salon', label: 'Salon' },
  { value: 'other', label: 'Lainnya' },
];

// Nilainya menyaring follow-up TERAKHIR, bukan "pernah". Tiga yang pertama dari
// phone_status, tiga berikutnya dari outcome_status — backend yang memisahkan.
//
// 'invalid' sengaja tidak ditawarkan: daftar lead memang membuang company yang
// follow-up terakhirnya 'invalid', jadi pilihan itu akan selalu kosong.
const FOLLOW_UP_STATUS_OPTIONS = [
  { value: '', label: 'Semua status' },
  { value: 'not_followed_up', label: 'Belum di-follow-up' },
  { value: 'connected', label: 'Terhubung' },
  { value: 'not_connected', label: 'Tidak terhubung' },
  { value: 'call_back_later', label: 'Minta dihubungi kembali' },
  { value: 'closing', label: 'Closing' },
  { value: 'other', label: 'Lainnya' },
];

// "Terbengkalai minimal N hari" — mencari yang sudah lama tidak disentuh, jadi
// yang belum pernah di-follow-up sama sekali ikut masuk di setiap pilihan.
const STALE_DAYS_OPTIONS = [
  { value: '', label: 'Kapan saja' },
  ...[1, 2, 3, 4, 5, 6, 7].map(d => ({ value: String(d), label: `Terbengkalai ≥ ${d} hari` })),
];

const Leads: React.FC = () => {
  const navigate = useNavigate();

  // URL adalah satu-satunya sumber kebenaran untuk halaman dan filter, sama
  // seperti halaman Companies. Sebelumnya `currentPage` hanya state komponen,
  // sehingga membuka sebuah lead lalu menekan Kembali me-mount ulang halaman
  // ini dari nol — pengguna di halaman 5 selalu terlempar balik ke halaman 1.
  const [searchParams, setSearchParams] = useSearchParams();

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const search = searchParams.get('search') || '';
  const businessType = searchParams.get('businessType') || '';
  const followUpStatus = searchParams.get('followUpStatus') || '';
  const staleDays = searchParams.get('lastFollowUpMinDaysAgo') || '';
  // Sales Dashboard links here with ?showClosing=1 to drill into the closing figure.
  const showClosing = searchParams.get('showClosing') === '1';

  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(search);
  const [total, setTotal] = useState(0);

  // Satu pintu untuk semua perubahan filter: apa pun yang menyaring daftar
  // harus mengembalikan halaman ke 1, kalau tidak pengguna mendarat di halaman
  // kosong karena hasil barunya lebih pendek.
  const updateParams = useCallback((changes: Record<string, string>, { resetPage = true } = {}) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([key, value]) => {
        if (value) next.set(key, value);
        else next.delete(key);
      });
      if (resetPage) next.set('page', '1');
      return next;
    });
  }, [setSearchParams]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getLeads({
        search: search || undefined,
        page: currentPage,
        perPage: PER_PAGE,
        businessType: businessType || undefined,
        showClosing: showClosing || undefined,
        followUpStatus: followUpStatus || undefined,
        lastFollowUpMinDaysAgo: staleDays ? parseInt(staleDays, 10) : undefined,
      });
      setLeads(res.data);
      setTotal(res.meta.total);
    } catch (err: any) {
      console.error('Error loading leads:', err);
      setLeads([]);
      setTotal(0);
      setError(err?.response?.data?.message || 'Gagal memuat daftar leads.');
    } finally {
      setLoading(false);
    }
  }, [search, businessType, showClosing, followUpStatus, staleDays, currentPage]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });

  const formatDateTime = (dateString: string): string =>
    new Date(dateString).toLocaleString('id-ID', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams({ search: searchInput.trim() });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <PhoneCall className="h-6 w-6 text-primary-600" />
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Leads</h1>
          <p className="mt-1 text-sm text-gray-500">Daftar perusahaan yang perlu di-follow-up.</p>
        </div>
      </div>

      <form onSubmit={handleSearchSubmit} className="flex flex-wrap gap-2 max-w-4xl">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Cari nama / brand / owner..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={businessType}
          onChange={e => updateParams({ businessType: e.target.value })}
          className="py-2 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          {BUSINESS_TYPE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={followUpStatus}
          onChange={e => updateParams({ followUpStatus: e.target.value })}
          className="py-2 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          {FOLLOW_UP_STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select
          value={staleDays}
          onChange={e => updateParams({ lastFollowUpMinDaysAgo: e.target.value })}
          className="py-2 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          {STALE_DAYS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700"
        >
          Cari
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={showClosing}
            onChange={e => updateParams({ showClosing: e.target.checked ? '1' : '' })}
            className="rounded border-gray-300"
          />
          Tampilkan yang closing
        </label>
      </form>

      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
      )}

      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Memuat leads..." />
        </div>
      ) : leads.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">{showClosing ? 'Belum ada company closing.' : 'Belum ada lead'}</h3>
          <p className="mt-1 text-sm text-gray-500">{showClosing ? 'Tidak ada data closing yang cocok dengan pencarian.' : 'Tidak ada lead yang cocok dengan pencarian.'}</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Company / Brand', 'Owner', 'Phone', 'Terdaftar', 'Follow-up terakhir', 'Langganan'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.map(lead => (
                  <tr
                    key={lead.id}
                    onClick={() => navigate(`/leads/${lead.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm">
                      <div className="font-medium text-gray-900">{lead.companyName}</div>
                      {lead.brandName && <div className="text-gray-500">{lead.brandName}</div>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{lead.ownerName || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{lead.phone || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(lead.createdAt)}
                      <div className="text-xs text-gray-400">{lead.ageDays} hari</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {lead.latestFollowUp ? (
                        <div>
                          <span className="font-medium text-gray-700">
                            {phoneStatusLabels[lead.latestFollowUp.phoneStatus] || lead.latestFollowUp.phoneStatus}
                          </span>
                          {lead.latestFollowUp.outcomeStatus && (
                            <span className="text-gray-500">
                              {' '}· {outcomeLabels[lead.latestFollowUp.outcomeStatus] || lead.latestFollowUp.outcomeStatus}
                            </span>
                          )}
                          <div className="text-xs text-gray-400">{formatDateTime(lead.latestFollowUp.at)}</div>
                        </div>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">
                          Belum di-follow-up
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {lead.subscription ? (
                        <div>
                          <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                            {lead.subscription.name || 'Berlangganan'}
                          </span>
                          {lead.subscribedAt && (
                            <div className="text-xs text-gray-400">Bayar pertama {formatDate(lead.subscribedAt)}</div>
                          )}
                          {lead.subscription.validUntil && (
                            <div className="text-xs text-gray-400">s/d {formatDate(lead.subscription.validUntil)}</div>
                          )}
                        </div>
                      ) : lead.subscribedAt ? (
                        // Pernah membayar tapi tidak punya langganan berbayar
                        // yang aktif sekarang — sudah habis dan belum diperpanjang.
                        <div>
                          <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            Langganan berakhir
                          </span>
                          <div className="text-xs text-gray-400">Bayar pertama {formatDate(lead.subscribedAt)}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && (
        <Pagination
          currentPage={currentPage}
          total={total}
          perPage={PER_PAGE}
          onPageChange={page => updateParams({ page: String(page) }, { resetPage: false })}
        />
      )}
    </div>
  );
};

export default Leads;
